import React, { useState } from "react";

import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BoltIcon from "@mui/icons-material/Bolt";
import PaletteIcon from "@mui/icons-material/Palette";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import useBetaLaunch from "app/hooks/globallyApplicable/useBetaLaunch";

const FEATURES = [
  {
    icon: <BoltIcon />,
    title: "Blazing fast",
    desc: "Rebuilt from the ground up for instant loads.",
  },
  {
    icon: <PaletteIcon />,
    title: "Beautiful & modern",
    desc: "A refreshed, cleaner interface with dark mode.",
  },
  {
    icon: <DashboardCustomizeIcon />,
    title: "Customizable",
    desc: "Reorder dashboard cards and tailor your workspace.",
  },
];

const shimmerKeyframes = {
  "@keyframes betaShimmer": {
    "0%": { backgroundPosition: "0% 50%" },
    "100%": { backgroundPosition: "200% 50%" },
  },
};

const floatKeyframes = {
  "@keyframes betaFloat": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-6px)" },
  },
};

const TryBetaButton = ({ compact = false }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [open, setOpen] = useState(false);
  const iconOnly = compact || isMobile;

  const { launchBeta } = useBetaLaunch();

  const handleLaunch = () => {
    launchBeta();
    setOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disableElevation
        startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
        sx={{
          ...shimmerKeyframes,
          textTransform: "none",
          fontWeight: 600,
          fontSize: 14,
          color: "#fff",
          borderRadius: "999px",
          px: iconOnly ? 1.25 : 1.75,
          py: 0.9,
          minWidth: 0,
          whiteSpace: "nowrap",
          background:
            "linear-gradient(110deg, #3c6eef, #7a5cff, #3c6eef)",
          backgroundSize: "200% 100%",
          animation: "betaShimmer 4s linear infinite",
          boxShadow: "0 4px 14px rgba(60, 110, 239, 0.35)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "& .MuiButton-startIcon": { mr: iconOnly ? 0 : 0.75 },
          "&:hover": {
            background: "linear-gradient(110deg, #345fd6, #6a4bd8, #345fd6)",
            backgroundSize: "200% 100%",
            transform: "translateY(-1px)",
            boxShadow: "0 6px 18px rgba(122, 92, 255, 0.45)",
          },
        }}
      >
        {!iconOnly && "Try Beta"}
        <Box
          component="span"
          sx={{
            ml: iconOnly ? 0 : 0.75,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.3px",
            px: 0.75,
            py: "2px",
            borderRadius: "999px",
            bgcolor: "rgba(255,255,255,0.22)",
            textTransform: "uppercase",
            lineHeight: 1.4,
          }}
        >
          New
        </Box>
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            overflow: "hidden",
            m: { xs: 1.5, sm: 2 },
          },
        }}
      >
        {/* Hero */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: 3,
            pt: 3.5,
            pb: 3.25,
            background:
              "linear-gradient(135deg, #0c2f49 0%, #23407a 45%, #6a4bd8 100%)",
          }}
        >
          {/* Ambient glows */}
          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              filter: "blur(40px)",
              opacity: 0.55,
              bgcolor: "#3c6eef",
              top: -60,
              right: -40,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 160,
              height: 160,
              borderRadius: "50%",
              filter: "blur(40px)",
              opacity: 0.55,
              bgcolor: "#7a5cff",
              bottom: -70,
              left: -30,
            }}
          />

          <IconButton
            onClick={() => setOpen(false)}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "rgba(255,255,255,0.85)",
              zIndex: 2,
              "&:hover": { background: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Stack
            alignItems="center"
            spacing={1.5}
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Box
              sx={{
                ...floatKeyframes,
                width: 62,
                height: 62,
                borderRadius: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.25)",
                backdropFilter: "blur(6px)",
                animation: "betaFloat 3.5s ease-in-out infinite",
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 30 }} />
            </Box>

            <Box textAlign="center">
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#fff",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                Introducing Ekalsutra v2
              </Box>
              <Typography
                sx={{
                  mt: 1.25,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: 20, sm: 24 },
                  lineHeight: 1.2,
                }}
              >
                A whole new ERP experience
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: { xs: 13, sm: 14 },
                  maxWidth: 380,
                  mx: "auto",
                }}
              >
                Faster, cleaner and more powerful — now available in beta for you
                to try.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 } }}>
          <Stack spacing={1.25}>
            {FEATURES.map((f) => (
              <Stack
                key={f.title}
                direction="row"
                alignItems="flex-start"
                spacing={1.5}
                sx={{
                  p: 1.25,
                  borderRadius: "14px",
                  bgcolor: "#f6f8fc",
                  border: "1px solid #eef2f8",
                }}
              >
                <Box
                  sx={{
                    flex: "none",
                    width: 38,
                    height: 38,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#3c6eef",
                    background:
                      "linear-gradient(135deg, #e8f0fe, #eae6ff)",
                    "& svg": { fontSize: 20 },
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: "#0c2f49",
                      lineHeight: 1.3,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12.5, color: "#5a7184", lineHeight: 1.35 }}
                  >
                    {f.desc}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.125}
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: "12px",
              fontSize: 12.5,
              fontWeight: 500,
              color: "#1a7a4c",
              bgcolor: "#e9f9f0",
              border: "1px solid #cdeedd",
            }}
          >
            <LockOpenIcon sx={{ fontSize: 18, flex: "none" }} />
            <span>
              You&apos;re already signed in — we&apos;ll carry your session over,
              no need to log in again.
            </span>
          </Stack>

          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.25}
            sx={{ mt: 2.5 }}
          >
            <Button
              fullWidth
              onClick={() => setOpen(false)}
              sx={{
                textTransform: "none",
                color: "#5A7184",
                fontWeight: 600,
                borderRadius: 2,
                py: 1.1,
                "&:hover": { background: "rgba(90,113,132,0.08)" },
              }}
            >
              Maybe later
            </Button>
            <Button
              fullWidth
              onClick={handleLaunch}
              endIcon={<ArrowForwardIcon />}
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                color: "#fff",
                borderRadius: "12px",
                py: 1.25,
                background: "linear-gradient(110deg, #3c6eef, #7a5cff)",
                boxShadow: "0 6px 16px rgba(60, 110, 239, 0.35)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  background: "linear-gradient(110deg, #345fd6, #6a4bd8)",
                  boxShadow: "0 8px 20px rgba(122, 92, 255, 0.45)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Try Beta
            </Button>
          </Stack>

          <Typography
            sx={{
              mt: 1.5,
              textAlign: "center",
              fontSize: 11.5,
              color: "#94A3B8",
            }}
          >
            Opens in a new tab · Your current ERP stays exactly as it is.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default TryBetaButton;
